<template>
  <div v-if="isInterne && assistantUrl">
    <!-- Bouton flottant (coin bas-droit ; le widget Freshdesk est déporté à gauche) -->
    <v-btn
      icon
      color="primary50"
      class="assistant-fab"
      :aria-label="open ? 'Fermer l\'assistant' : 'Ouvrir l\'assistant'"
      @click="togglePanel">
      <v-icon>{{ open ? 'fas fa-xmark' : 'fas fa-comment-dots' }}</v-icon>
    </v-btn>

    <!-- Panneau de chat -->
    <v-card v-if="open" class="assistant-panel" :class="{ 'assistant-panel--expanded': expanded }" elevation="10">
      <v-toolbar density="comfortable" color="primary50" flat>
        <v-icon class="ml-3" size="20">fas fa-robot</v-icon>
        <v-toolbar-title style="font-size: 16px; font-weight: 600;">
          Comment puis-je vous aider ?
          <span class="assistant-beta" title="Fonctionnalité en cours d'évaluation">BETA</span>
        </v-toolbar-title>
        <v-menu location="bottom end">
          <template #activator="{ props }">
            <v-btn icon size="small" variant="text" v-bind="props" aria-label="Options">
              <v-icon>fas fa-ellipsis-vertical</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item @click="expanded = !expanded">
              <template #prepend>
                <v-icon size="16">{{ expanded ? 'fas fa-down-left-and-up-right-to-center' : 'fas fa-up-right-and-down-left-from-center' }}</v-icon>
              </template>
              <v-list-item-title>{{ expanded ? 'Réduire la fenêtre' : 'Agrandir la fenêtre' }}</v-list-item-title>
            </v-list-item>
            <v-list-item :disabled="messages.length === 0" @click="downloadTranscript">
              <template #prepend>
                <v-icon size="16">fas fa-download</v-icon>
              </template>
              <v-list-item-title>Télécharger la transcription</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>

      <div ref="scroller" class="assistant-messages">
        <div v-if="messages.length === 0" class="assistant-hint">
          Posez une question sur vos données (établissements, contrats, paies…). Réponses en
          lecture seule, avec vos droits d'accès.
          <strong>Attention, l'IA peut faire des erreurs.</strong>
        </div>
        <template v-for="(m, i) in messages" :key="i">
          <div v-if="m.role === 'user'" class="assistant-bubble is-user">{{ m.content }}</div>
          <div v-else class="assistant-bubble is-assistant">
            <!-- Contenu markdown -> HTML (échappé puis balises en liste blanche). -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-if="m.content" class="assistant-md" @click="onFicheClick" v-html="renderMarkdown(shown(i))"></div>
            <!-- Attente de la réponse : trois points animés en décalé, SEULEMENT tant qu'elle est en
                 cours. Une bulle vide dont le flux est terminé n'attend plus rien : sans cette
                 condition, un échec silencieux laissait les points tourner indéfiniment. -->
            <span
              v-else-if="streaming && i === messages.length - 1"
              class="assistant-typing"
              role="status"
              aria-label="L'assistant prépare sa réponse">
              <span class="assistant-dot"></span>
              <span class="assistant-dot"></span>
              <span class="assistant-dot"></span>
            </span>
            <span v-else class="assistant-empty">Aucune réponse reçue.</span>
            <!-- Copie du markdown brut, et non du HTML rendu : c'est lui qui se recolle
                 proprement ailleurs. Masqué tant que la réponse en cours s'écrit. -->
            <div v-if="m.content && !(streaming && i === messages.length - 1)" class="assistant-actions">
              <button
                type="button"
                class="assistant-copy"
                :aria-label="copiedIndex === i ? 'Réponse copiée' : 'Copier la réponse'"
                :title="copiedIndex === i ? 'Copié' : 'Copier la réponse'"
                @click="copyMessage(i, shown(i))">
                <v-icon size="12">{{ copiedIndex === i ? 'fas fa-check' : 'fas fa-copy' }}</v-icon>
                <span>{{ copiedIndex === i ? 'Copié' : 'Copier' }}</span>
              </button>
            </div>
          </div>
        </template>
        <div v-if="currentTool" class="assistant-tool">
          <v-icon size="14">fas fa-magnifying-glass</v-icon> {{ currentTool }}…
        </div>
        <div v-if="error" class="assistant-error">{{ error }}</div>
      </div>

      <div v-if="micError" class="assistant-mic-error">{{ micError }}</div>
      <div class="assistant-input">
        <v-textarea
          v-model="input"
          rows="1"
          auto-grow
          max-rows="5"
          density="compact"
          variant="outlined"
          hide-details
          placeholder="Votre question…"
          :disabled="streaming"
          @keydown.enter.exact.prevent="send"></v-textarea>
        <v-btn
          v-if="speechSupported"
          icon
          size="small"
          :color="recording ? 'error50' : 'grey60'"
          :class="{ 'assistant-mic--rec': recording }"
          :disabled="streaming"
          :aria-label="recording ? 'Arrêter la dictée' : 'Dicter la question'"
          :title="recording ? 'Arrêter la dictée' : 'Dicter la question'"
          @click="toggleMic">
          <v-icon>fas fa-microphone</v-icon>
        </v-btn>
        <v-btn
          v-if="!streaming"
          icon
          size="small"
          color="primary50"
          :disabled="!input.trim()"
          @click="send">
          <v-icon>fas fa-paper-plane</v-icon>
        </v-btn>
        <v-btn v-else icon size="small" color="error50" @click="stop">
          <v-icon>fas fa-stop</v-icon>
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import { storeSession } from '@/generated/stores';
import UserProfile from '@/framework/base/classes/UserProfile';
import Route from '@/generated/classes/Route';
import { Fiches, Modules } from '@/generated/classes/AppSchema';
import { goToModule } from '@/framework/base/helpers/NavHelper';
import {
  FICHE_LINK_RE,
  type EntityEntry,
  ficheAnchor,
  fold,
  linkifyNames,
  asksForInternalId,
  collapseRepeatedParagraphs,
  stripInternalIds
} from '@/custom/helpers/assistantLinks';

interface AssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Classes dont la fiche est navigable, avec leur cible (module + fiche). Les couples sont
// ceux utilisés par les listes de l'application : SectionSalarieListExtend,
// SectionEtablissementListExtend et SectionContratSalarieListExtend. Le projet suit le
// clic sur une ligne de SectionProjetList (Fiches.PROJET + id du projet, ouvert par
// ModuleProjet dans FicheProjetExtend) : Fiches.PROJET_LIST est la LISTE des projets, pas
// un projet, et PROJET_CONTRATS + ficheObjId ouvre un contrat. Une classe absente de cette
// table n'est PAS transformée en lien (cf. inline) : pas de lien mort.
const FICHE_TARGETS: Record<string, { module: string; fiche: string }> = {
  salarie: { module: Modules.SALARIE, fiche: Fiches.SALARIE_ABSTRACT },
  etablissement: { module: Modules.ETABLISSEMENT, fiche: Fiches.ETABLISSEMENT_ABSTRACT },
  contrat: { module: Modules.PROJET, fiche: Fiches.PROJET_CONTRATS },
  projet: { module: Modules.PROJET, fiche: Fiches.PROJET }
};

function escapeHtml (s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Formatage inline sur du texte DÉJÀ échappé : code, gras, italique, liens.
function inline (text: string, entries: EntityEntry[]): string {
  let t = text;
  t = t.replace(/`([^`]+)`/g, '<code>$1</code>');
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  t = t.replace(/\b_([^_\n]+)_\b/g, '<em>$1</em>');
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  // Lien interne explicite. L'ancre est inerte (href="#") et ne porte que la classe et l'id
  // dans des data-* ; la navigation est faite par onFicheClick. La classe et l'id sont
  // contraints par la regex, et le libellé est déjà échappé : rien d'injectable ici.
  t = t.replace(FICHE_LINK_RE, (_whole, label: string, cls: string, id: string) => {
    const key = cls.toLowerCase();
    return FICHE_TARGETS[key] ? ficheAnchor(key, Number(id), label) : label;
  });
  // Puis les noms annoncés par l'hôte, en dernier pour ne pas imbriquer de lien.
  return linkifyNames(t, entries);
}

export default defineComponent({
  name: 'SectionAssistant',
  data () {
    const messages: AssistantMessage[] = [];
    return {
      open: false,
      messages,
      input: '',
      streaming: false,
      // Vrai dès que l'hôte a envoyé un événement de fin (done ou error) : un flux qui se ferme sans
      // l'un ni l'autre s'est interrompu en route.
      sawEnd: false,
      currentTool: '',
      error: '',
      abortController: null as AbortController | null,
      recording: false,
      recognition: null as any,
      micBase: '',
      micError: '',
      expanded: false,
      // Index du message dont la copie vient d'aboutir (-1 = aucun), et minuterie qui remet
      // le bouton dans son état normal.
      copiedIndex: -1,
      copyTimer: null as ReturnType<typeof setTimeout> | null,
      // Enregistrements annoncés par l'hôte (événement SSE "entities"), indexés par nom replié.
      // Une valeur null marque un homonyme visant deux fiches différentes : on ne lie pas.
      entities: {} as Record<string, { cls: string; id: number } | null>
    };
  },
  computed: {
    // Noms liables, les plus longs d'abord pour que "Benjamin Millepied" gagne sur "Millepied".
    entityEntries (): EntityEntry[] {
      const out: EntityEntry[] = [];
      for (const [folded, v] of Object.entries(this.entities)) {
        if (v) {
          out.push({ folded, cls: v.cls, id: v.id });
        }
      }
      return out.sort((a, b) => b.folded.length - a.folded.length);
    },
    isInterne (): boolean {
      const user = storeSession.connectedUser;
      return user != null && UserProfile.IsInterne(user);
    },
    assistantUrl (): string {
      return import.meta.env.VITE_VUE_APP_ASSISTANT_URL ?? '';
    },
    speechSupported (): boolean {
      return typeof window !== 'undefined' &&
        !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
    }
  },
  unmounted () {
    this.stop();
    this.stopMic();
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
      this.copyTimer = null;
    }
  },
  methods: {
    // Enregistrements annoncés par l'hôte au fil des appels d'outils. Réaffectation de l'objet
    // (et non mutation) pour que le rendu markdown soit recalculé.
    addEntities (items: unknown): void {
      if (!Array.isArray(items)) {
        return;
      }
      const next = { ...this.entities };
      let changed = false;
      for (const raw of items) {
        const it = raw as { class?: string; id?: number; name?: string };
        const cls = String(it?.class ?? '').toLowerCase();
        const id = Number(it?.id);
        const name = String(it?.name ?? '').trim();
        if (!FICHE_TARGETS[cls] || !Number.isInteger(id) || id <= 0 || name.length < 3) {
          continue;
        }
        const key = fold(name);
        const prev = next[key];
        if (prev === undefined) {
          next[key] = { cls, id };
          changed = true;
        } else if (prev !== null && (prev.cls !== cls || prev.id !== id)) {
          next[key] = null; // homonymes vers des fiches différentes : on ne devine pas
          changed = true;
        }
      }
      if (changed) {
        this.entities = next;
      }
    },
    // Clic sur un lien de fiche produit par l'assistant : navigue vers la fiche concernée.
    // Délégation sur le conteneur (le HTML est injecté par v-html, pas de @click par ancre).
    async onFicheClick (ev: MouseEvent): Promise<void> {
      const anchor = (ev.target as HTMLElement | null)?.closest('[data-ci-class]') as HTMLElement | null;
      if (!anchor) {
        return;
      }
      ev.preventDefault();
      const dest = FICHE_TARGETS[anchor.getAttribute('data-ci-class') ?? ''];
      const id = Number(anchor.getAttribute('data-ci-id'));
      if (!dest || !Number.isInteger(id) || id <= 0) {
        return;
      }
      // Même construction que les listes de l'application (module + fiche + ficheObjId).
      const route = new Route();
      route.module = dest.module;
      route.fiche = dest.fiche;
      route.ficheObjId = id;
      // Déjà dans le module de destination (ex. fiche d'un salarié -> fiche d'un autre) : la
      // RouterView et le FicheContainer n'ont pas de key, Vue réutilise donc la fiche affichée
      // sans la recharger et l'on reste sur l'ancien enregistrement. Un passage par l'accueil
      // force un montage neuf, comme le fait l'utilisateur à la main ; le tick laisse le module
      // se démonter avant de rouvrir le suivant (deux navigations dans le même rendu seraient
      // fusionnées et réutiliseraient encore l'instance).
      if (this.$router.currentRoute.value.name === dest.module.toLowerCase()) {
        await this.$router.push({ name: 'Dashboard' });
        await this.$nextTick();
      }
      goToModule(this.$router, route);
    },
    // Copie une réponse dans le presse-papiers, puis bascule le bouton en « Copié »
    // le temps d'une confirmation visible.
    async copyMessage (index: number, text: string): Promise<void> {
      if (!await this.writeClipboard(text)) {
        this.error = "Copie impossible : le navigateur a refusé l'accès au presse-papiers.";
        return;
      }
      this.copiedIndex = index;
      if (this.copyTimer) {
        clearTimeout(this.copyTimer);
      }
      this.copyTimer = setTimeout(() => {
        this.copiedIndex = -1;
        this.copyTimer = null;
      }, 1600);
    },
    // navigator.clipboard n'existe qu'en contexte sécurisé (https, ou localhost en
    // développement). Repli sur execCommand ailleurs : obsolète, mais c'est la seule
    // voie restante, et un échec silencieux serait pire.
    async writeClipboard (text: string): Promise<boolean> {
      try {
        if (window.isSecureContext && navigator.clipboard) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch {
        // permission refusée ou API indisponible : on tente le repli
      }
      try {
        const zone = document.createElement('textarea');
        zone.value = text;
        zone.setAttribute('readonly', '');
        zone.style.position = 'fixed';
        zone.style.opacity = '0';
        document.body.appendChild(zone);
        zone.select();
        const copie = document.execCommand('copy');
        document.body.removeChild(zone);
        return copie;
      } catch {
        return false;
      }
    },
    // Texte d'un message tel que l'utilisateur doit le voir (affichage, copie, export) :
    // sans les identifiants internes étiquetés, sauf si sa question en demandait un. Le
    // texte brut reste dans `messages`, donc dans l'historique renvoyé au modèle.
    shown (index: number): string {
      const m = this.messages[index];
      if (!m || m.role !== 'assistant') {
        return m ? m.content : '';
      }
      // Les paragraphes répétés d'un tour à l'autre sont retirés à l'affichage, comme les identifiants.
      let asked = false;
      for (let k = index - 1; k >= 0; k--) {
        if (this.messages[k].role === 'user') {
          asked = asksForInternalId(this.messages[k].content);
          break;
        }
      }
      return collapseRepeatedParagraphs(asked ? m.content : stripInternalIds(m.content));
    },
    // Rendu markdown -> HTML sûr : on échappe tout le HTML, puis on ré-injecte
    // uniquement les balises que l'on génère (titres, listes, tableaux, gras…).
    renderMarkdown (raw: string): string {
      const entries = this.entityEntries;
      const lines = escapeHtml(raw).replace(/\r\n/g, '\n').split('\n');
      const out: string[] = [];
      let i = 0;
      const isTableSep = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
      const parseCells = (l: string) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

      while (i < lines.length) {
        const line = lines[i];

        // Tableau GFM : ligne | … | suivie d'une ligne séparatrice.
        if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
          const header = parseCells(line);
          i += 2;
          const body: string[][] = [];
          while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
            body.push(parseCells(lines[i]));
            i++;
          }
          let t = '<table class="assistant-table"><thead><tr>';
          t += header.map((c) => `<th>${inline(c, entries)}</th>`).join('');
          t += '</tr></thead><tbody>';
          for (const r of body) {
            t += '<tr>' + r.map((c) => `<td>${inline(c, entries)}</td>`).join('') + '</tr>';
          }
          t += '</tbody></table>';
          out.push(t);
          continue;
        }

        // Titre.
        const h = line.match(/^(#{1,6})\s+(.*)$/);
        if (h) {
          const level = Math.min(h[1].length + 2, 6); // # -> h3
          out.push(`<h${level}>${inline(h[2], entries)}</h${level}>`);
          i++;
          continue;
        }

        // Liste à puces.
        if (/^\s*[-*]\s+/.test(line)) {
          const items: string[] = [];
          while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
            items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ''), entries)}</li>`);
            i++;
          }
          out.push(`<ul>${items.join('')}</ul>`);
          continue;
        }

        // Liste numérotée.
        if (/^\s*\d+\.\s+/.test(line)) {
          const items: string[] = [];
          while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
            items.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s+/, ''), entries)}</li>`);
            i++;
          }
          out.push(`<ol>${items.join('')}</ol>`);
          continue;
        }

        // Ligne vide -> séparateur de paragraphe.
        if (line.trim() === '') {
          i++;
          continue;
        }

        // Paragraphe : regroupe les lignes consécutives non vides.
        const para: string[] = [];
        while (
          i < lines.length &&
          lines[i].trim() !== '' &&
          !/^(#{1,6})\s/.test(lines[i]) &&
          !/^\s*[-*]\s+/.test(lines[i]) &&
          !/^\s*\d+\.\s+/.test(lines[i]) &&
          !(lines[i].includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1]))
        ) {
          para.push(inline(lines[i], entries));
          i++;
        }
        out.push(`<p>${para.join('<br>')}</p>`);
      }
      return out.join('');
    },
    // Enregistrements annoncés par l'hôte au fil des appels d'outils. Réaffectation de l'objet
    // (et non mutation) pour que le rendu markdown soit recalculé.
    addEntities (items: unknown): void {
      if (!Array.isArray(items)) {
        return;
      }
      const next = { ...this.entities };
      let changed = false;
      for (const raw of items) {
        const it = raw as { class?: string; id?: number; name?: string };
        const cls = String(it?.class ?? '').toLowerCase();
        const id = Number(it?.id);
        const name = String(it?.name ?? '').trim();
        if (!FICHE_TARGETS[cls] || !Number.isInteger(id) || id <= 0 || name.length < 3) {
          continue;
        }
        const key = fold(name);
        const prev = next[key];
        if (prev === undefined) {
          next[key] = { cls, id };
          changed = true;
        } else if (prev !== null && (prev.cls !== cls || prev.id !== id)) {
          next[key] = null; // homonymes vers des fiches différentes : on ne devine pas
          changed = true;
        }
      }
      if (changed) {
        this.entities = next;
      }
    },
    // Clic sur un lien de fiche produit par l'assistant : navigue vers la fiche concernée.
    // Délégation sur le conteneur (le HTML est injecté par v-html, pas de @click par ancre).
    onFicheClick (ev: MouseEvent): void {
      const anchor = (ev.target as HTMLElement | null)?.closest('[data-ci-class]') as HTMLElement | null;
      if (!anchor) {
        return;
      }
      ev.preventDefault();
      const dest = FICHE_TARGETS[anchor.getAttribute('data-ci-class') ?? ''];
      const id = Number(anchor.getAttribute('data-ci-id'));
      if (!dest || !Number.isInteger(id) || id <= 0) {
        return;
      }
      // Même construction que les listes de l'application (module + fiche + ficheObjId).
      const route = new Route();
      route.module = dest.module;
      route.fiche = dest.fiche;
      route.ficheObjId = id;
      goToModule(this.$router, route);
    },
    togglePanel () {
      this.open = !this.open;
    },
    // Télécharge la conversation courante en fichier texte.
    downloadTranscript () {
      if (this.messages.length === 0) {
        return;
      }
      const body = this.messages
        // Les liens de fiche sont réduits à leur libellé : la syntaxe ci: n'a pas de sens
        // hors de l'application.
        .map((m, i) => `${m.role === 'user' ? 'Vous' : 'Assistant'} :\n` +
          `${this.shown(i).replace(FICHE_LINK_RE, '$1')}\n`)
        .join('\n');
      const now = new Date();
      const header = `Assistant CI — transcription\n${now.toLocaleString('fr-FR')}\n\n`;
      // Nom de fichier : CI_AAAAMMJJHHMM.txt (heure locale).
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
        `${pad(now.getHours())}${pad(now.getMinutes())}`;
      const blob = new Blob([header + body], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CI_${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    // Dictée vocale via l'API Web Speech du navigateur (fr-FR). Remplit le champ
    // pour relecture — l'envoi reste manuel. Aucun backend, aucune clé.
    toggleMic () {
      if (this.recording) {
        this.stopMic();
      } else {
        this.startMic();
      }
    },
    startMic () {
      const Ctor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!Ctor) {
        return;
      }
      this.micError = '';
      const rec = new Ctor();
      rec.lang = 'fr-FR';
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;
      this.micBase = this.input;
      rec.onresult = (event: any) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        this.input = (this.micBase ? this.micBase + ' ' : '') + transcript;
      };
      rec.onerror = (event: any) => {
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          this.micError = 'Micro refusé ou indisponible. Autorisez le micro dans le navigateur.';
        } else if (event.error === 'no-speech') {
          this.micError = 'Aucune parole détectée.';
        }
      };
      rec.onend = () => {
        this.recording = false;
        this.recognition = null;
      };
      this.recognition = rec;
      try {
        rec.start();
        this.recording = true;
      } catch {
        this.recording = false;
        this.recognition = null;
      }
    },
    stopMic () {
      if (this.recognition) {
        try {
          this.recognition.stop();
        } catch {
          // reconnaissance déjà arrêtée
        }
      }
      this.recording = false;
    },
    stop () {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.streaming = false;
      this.currentTool = '';
    },
    async scrollToBottom () {
      await nextTick();
      const el = this.$refs.scroller as HTMLElement | undefined;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    async send () {
      const text = this.input.trim();
      if (!text || this.streaming) {
        return;
      }
      this.stopMic();
      this.error = '';
      this.micError = '';
      this.input = '';
      this.messages.push({ role: 'user', content: text });
      const assistant: AssistantMessage = { role: 'assistant', content: '' };
      this.messages.push(assistant);
      this.streaming = true;
      this.sawEnd = false;
      await this.scrollToBottom();

      this.abortController = new AbortController();
      try {
        // fetch + ReadableStream : EventSource ne fait pas de POST.
        const res = await fetch(`${this.assistantUrl}/api/assistant/chat`, {
          method: 'POST',
          headers: {
            Authorization: storeSession.httpToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            messages: this.messages
              .slice(0, -1) // on n'envoie pas la bulle assistant vide en cours
              .map((m) => ({ role: m.role, content: m.content }))
          }),
          signal: this.abortController.signal
        });
        if (!res.ok || !res.body) {
          this.error = `Assistant indisponible (HTTP ${res.status}).`;
          this.streaming = false;
          return;
        }
        await this.readSse(res.body, assistant);
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          this.error = "Erreur de connexion à l'assistant.";
        }
      } finally {
        this.streaming = false;
        this.currentTool = '';
        this.abortController = null;
        await this.scrollToBottom();
      }
    },
    async readSse (body: ReadableStream<Uint8Array>, assistant: AssistantMessage) {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) {
          // Fermeture sans événement de fin : coupure réseau, hôte arrêté... L'utilisateur doit le savoir.
          if (!this.sawEnd) {
            this.error = "La connexion avec l'assistant s'est interrompue avant la fin de la réponse.";
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        // Événements SSE séparés par une ligne vide.
        let sep = buffer.indexOf('\n\n');
        while (sep !== -1) {
          const chunk = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          this.handleSseEvent(chunk, assistant);
          sep = buffer.indexOf('\n\n');
        }
      }
    },
    handleSseEvent (chunk: string, assistant: AssistantMessage) {
      let event = 'message';
      let data = '';
      for (const line of chunk.split('\n')) {
        if (line.startsWith('event:')) {
          event = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          data += line.slice(5).trim();
        }
      }
      if (!data) {
        return;
      }
      let payload: any;
      try {
        payload = JSON.parse(data);
      } catch {
        return;
      }
      if (event === 'delta') {
        assistant.content += payload.text ?? '';
        this.currentTool = '';
        this.scrollToBottom();
      } else if (event === 'tool_use') {
        // L'hôte envoie un libellé lisible (« Liste : Contrats de travail »), dérivé du modèle
        // JSON. On retombe sur le nom technique si l'hôte est d'une version antérieure.
        this.currentTool = payload.title ?? payload.name ?? '';
        this.scrollToBottom();
      } else if (event === 'tool_result') {
        this.currentTool = '';
      } else if (event === 'entities') {
        this.addEntities(payload.items);
      } else if (event === 'done') {
        this.sawEnd = true;
        // L'hôte s'arrête après un nombre maximal de tours d'outils : sans ce message, une question
        // qui demande trop d'étapes se terminait sans un mot.
        if (payload.stopReason === 'max_turns') {
          this.error = "La question demande trop d'étapes pour une seule réponse (limite atteinte). "
            + 'Précisez-la ou découpez-la en plusieurs questions.';
        }
      } else if (event === 'error') {
        this.sawEnd = true;
        this.error = payload.status === 401
          ? 'Votre session a expiré. Rechargez la page pour vous reconnecter.'
          : (payload.message ?? "Erreur de l'assistant.");
      }
    }
  }
});
</script>

<style scoped>
.assistant-fab {
  position: fixed;
  bottom: 48px; /* dégage le footer "Appli test" affiché hors production */
  right: 20px;
  z-index: 2001;
}

.assistant-panel {
  position: fixed;
  bottom: 116px;
  right: 20px;
  z-index: 2001;
  width: 520px;
  max-width: calc(100vw - 32px);
  height: 78vh;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  transition: width 0.15s ease, height 0.15s ease;
}

.assistant-panel--expanded {
  width: 820px;
  height: calc(100vh - 132px);
  max-height: calc(100vh - 132px);
}

.assistant-messages {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgb(var(--v-theme-grey97));
}

.assistant-hint {
  font-size: 13px;
  color: rgb(var(--v-theme-grey40));
  line-height: 1.5;
}

.assistant-bubble {
  max-width: 88%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.assistant-bubble.is-user {
  align-self: flex-end;
  background: rgb(var(--v-theme-primary50));
  color: rgb(var(--v-theme-white));
  border-bottom-right-radius: 3px;
  white-space: pre-wrap;
}

.assistant-bubble.is-assistant {
  align-self: flex-start;
  background: rgb(var(--v-theme-white));
  color: rgb(var(--v-theme-grey10));
  border: 1px solid rgb(var(--v-theme-grey90));
  border-bottom-left-radius: 3px;
}

/* Bouton de copie : discret au repos, net au survol de la bulle ou au focus clavier. */
.assistant-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.assistant-copy {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border: 1px solid rgb(var(--v-theme-grey90));
  border-radius: 6px;
  background: transparent;
  color: rgb(var(--v-theme-grey40));
  font-size: 11px;
  line-height: 1.7;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.assistant-bubble.is-assistant:hover .assistant-copy,
.assistant-copy:focus-visible {
  opacity: 1;
}

.assistant-copy:hover {
  border-color: rgb(var(--v-theme-grey60));
  color: rgb(var(--v-theme-grey10));
}

/* Pastille BETA dans la barre de titre (fond primary50, d'où le blanc translucide). */
.assistant-beta {
  display: inline-block;
  vertical-align: 2px;
  margin-left: 6px;
  padding: 1px 6px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  line-height: 1.4;
  text-transform: uppercase;
  opacity: 0.9;
}

/* Bulle terminée sans réponse (flux interrompu) : ni points animés ni bulle vide. */
.assistant-empty {
  font-style: italic;
  opacity: 0.7;
}

/* Attente de la réponse : trois points qui rebondissent en décalé. */
.assistant-typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 16px;
  padding: 0 2px;
}

.assistant-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-grey40));
  animation: assistant-dot-bounce 1.2s ease-in-out infinite;
}

.assistant-dot:nth-child(2) {
  animation-delay: 0.16s;
}

.assistant-dot:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes assistant-dot-bounce {
  0%, 70%, 100% {
    transform: translateY(0);
    opacity: 0.3;
  }

  35% {
    transform: translateY(-5px);
    opacity: 1;
  }
}

@keyframes assistant-dot-fade {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* Accessibilité : pas de déplacement si l'utilisateur a désactivé les animations — on garde
   un clignotement d'opacité pour que l'attente reste perceptible. */
@media (prefers-reduced-motion: reduce) {
  .assistant-dot {
    animation: assistant-dot-fade 1.4s ease-in-out infinite;
  }
}

.assistant-tool {
  align-self: flex-start;
  font-size: 12px;
  color: rgb(var(--v-theme-secondary40));
  font-style: italic;
}

.assistant-error {
  font-size: 13px;
  color: rgb(var(--v-theme-primary50));
}

.assistant-input {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid rgb(var(--v-theme-grey90));
  background: rgb(var(--v-theme-white));
}

.assistant-mic-error {
  font-size: 12px;
  color: rgb(var(--v-theme-primary50));
  padding: 0 14px 4px;
  background: rgb(var(--v-theme-white));
}

.assistant-mic--rec {
  animation: assistant-mic-pulse 1.1s ease-in-out infinite;
}

@keyframes assistant-mic-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.65; }
}

/* Mise en forme du markdown rendu */
.assistant-md :deep(p) {
  margin: 0 0 8px;
}

.assistant-md :deep(p:last-child) {
  margin-bottom: 0;
}

.assistant-md :deep(h3),
.assistant-md :deep(h4),
.assistant-md :deep(h5) {
  margin: 10px 0 6px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary40));
}

.assistant-md :deep(ul),
.assistant-md :deep(ol) {
  margin: 4px 0 8px;
  padding-left: 20px;
}

.assistant-md :deep(li) {
  margin: 2px 0;
}

.assistant-md :deep(code) {
  background: rgb(var(--v-theme-grey95));
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.9em;
}

.assistant-md :deep(a) {
  color: rgb(var(--v-theme-action40));
  text-decoration: underline;
}

/* Lien vers une fiche de l'application : signalé par un chevron, pour le distinguer
   d'un lien web externe. */
.assistant-md :deep(.assistant-fiche-link) {
  font-weight: 600;
  cursor: pointer;
  text-decoration-style: dotted;
}

.assistant-md :deep(.assistant-fiche-link)::after {
  content: '\203A'; /* › */
  margin-left: 3px;
  font-weight: 700;
}

.assistant-md :deep(.assistant-fiche-link):hover {
  text-decoration-style: solid;
}

.assistant-md :deep(.assistant-table) {
  border-collapse: collapse;
  width: 100%;
  margin: 6px 0 10px;
  font-size: 13px;
}

.assistant-md :deep(.assistant-table th),
.assistant-md :deep(.assistant-table td) {
  border: 1px solid rgb(var(--v-theme-grey90));
  padding: 5px 9px;
  text-align: left;
  vertical-align: top;
}

.assistant-md :deep(.assistant-table th) {
  background: rgb(var(--v-theme-action90));
  color: rgb(var(--v-theme-action30));
  font-weight: 600;
}

.assistant-md :deep(.assistant-table tr:nth-child(even) td) {
  background: rgb(var(--v-theme-grey97));
}
</style>
